import { useContext } from 'react'

import {
  Button, Form, Grid, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'
import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useSelectLayoutTemplateModal from './hooks/useSelectLayoutTemplateModal'

import LayoutTemplateTile from './components/LayoutTemplateTile'

const MODAL_KEY = 'SelectLayoutTemplateModal'

type ModalPayload = {
  callbacks: {
    selectLayoutTemplateId: (payload: HandleCallbackActionParams) => void,
  },
  layoutTemplateId: number,
  publicationId: number,
  publicationProductId: number,
}

type ModalCallbacks = {
  closeModal: () => void,
  selectLayoutTemplateId: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const SelectLayoutTemplateModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload: ModalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: modalPayloadCallbacks,
    layoutTemplateId,
    publicationId,
    publicationProductId,
  } = modalPayload

  const {
    selectLayoutTemplateId,
  } = modalPayloadCallbacks || {}

  const selectLayoutTemplatePayload = useSelectLayoutTemplateModal({
    layoutTemplateId,
    publicationId,
    publicationProductId,
  })

  const {
    callbacks: {
      setState,
    },
    layoutTemplatesPayload: {
      filteredLayoutTemplates,
    },
    saveEnabled,
    sortedPublicationProducts,
    state: {
      selectedLayoutTemplateId,
      selectedPublicationProductId,
    },
  } = selectLayoutTemplatePayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Select" titleSecondLine="Layout Template" />

      <SidebarModal.Content hasFooter={false}>
        <Form>
          <Form.Field label="Page Size">
            <select
              name="publication_product_id"
              onChange={e => setState({
                selectedLayoutTemplateId: undefined,
                // @ts-expect-error TS2322
                selectedPublicationProductId: e.target.value,
              })}
              value={selectedPublicationProductId}
            >
              <option value="">Please Select...</option>
              {sortedPublicationProducts.map(publicationProduct => (
                <option key={publicationProduct.id} value={publicationProduct.id}>
                  {publicationProduct.title}
                </option>
              ))}
            </select>
          </Form.Field>

          {!!selectedPublicationProductId && (
            <Form.Field label="Layout Template" marginTop="large">
              <Grid
                // @ts-expect-error TS2322
                gridGap="medium"
                gridAutoRows="1fr"
                gridTemplateColumns="repeat(auto-fill, minmax(160px, 1fr))"
                gridTemplateRows="repeat(auto-fill, minmax(160px, 1fr))"
              >
                {filteredLayoutTemplates.map(layoutTemplate => (
                  <LayoutTemplateTile
                    callbacks={{
                      selectLayoutTemplate: () => setState({ selectedLayoutTemplateId: layoutTemplate.id }),
                    }}
                    key={layoutTemplate.id}
                    layoutTemplate={layoutTemplate}
                    selected={layoutTemplate.id === selectedLayoutTemplateId}
                  />
                ))}
              </Grid>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loadingBubbleColor="white"
          onClick={() => selectLayoutTemplateId({
            // @ts-expect-error TS2741
            callbacks: {
              afterAction: closeModal,
            },
            entityParams: {
              layout_template_id: selectedLayoutTemplateId,
              publication_product_id: selectedPublicationProductId,
            },
            toastText: 'Layout Template Updated',
          })}
          size="large"
        >
          {saveEnabled ? 'Select Layout Template' : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SelectLayoutTemplateModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
